/**
 * Медиа сданного отчёта: посмотреть, что залито, снять лишнее, доложить недостающее.
 *
 * Исключение из A45: сам отчёт после сдачи не правится (деньги, peer-review, вейверы —
 * только через возврат OO), но медиа заливают уже ПОСЛЕ смены и часто не с первого раза.
 * Ошибка тут видна игрокам, поэтому оператор чинит её сам, не дёргая DC.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { api } from "../../api/client";
import css from "./reports.module.css";

interface Material {
  id: number;
  kind: string;
  label: string;
  duration: string;
  uploader: string;
  mine: number;
  /** Подписанные превью/стрим (26.08, владелец): смотреть прямо в боте. */
  poster: string | null;
  stream: string | null;
}

const KIND_WORD: Record<string, string> = { video: "видео", photo: "фото", audio: "аудио" };

/** Внутренний просмотрщик (26.08): видео/фото прямо в мини-аппе, без переходов. */
function MediaView({ m, onClose }: { m: Material; onClose: () => void }): ReactElement {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className={css.mvOverlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div className={css.mvFrame} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={css.mvClose} onClick={onClose} aria-label="Закрыть">✕</button>
        {m.kind === "video"
          ? <video className={css.mvMedia} controls autoPlay playsInline poster={m.poster ?? undefined} src={m.stream ?? undefined} />
          : m.kind === "audio"
            ? <audio controls autoPlay src={m.stream ?? undefined} />
            : <img className={css.mvMedia} src={m.stream ?? m.poster ?? ""} alt={m.label} />}
      </div>
    </div>
  );
}

export function ReportMedia({ bookingId }: { bookingId: number }): ReactElement {
  const [items, setItems] = useState<Material[] | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [view, setView] = useState<Material | null>(null);
  // Тостов на экране оператора нет — отвечаем строкой прямо под блоком.
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    api<{ materials: Material[] }>(`/api/game-reports/${bookingId}/materials`)
      .then((r) => { setItems(r.materials ?? []); setError(""); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Не удалось загрузить медиа"));
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (m: Material): Promise<void> => {
    setBusyId(m.id);
    try {
      await api(`/api/game-reports/${bookingId}/material/${m.id}`, { method: "DELETE" });
      setNote(`${KIND_WORD[m.kind] ?? "Файл"} «${m.label}» снят с игры`);
      setConfirmId(null);
      load();
    } catch (e) {
      setNote(e instanceof Error ? e.message : "Не удалось удалить");
    } finally {
      setBusyId(null);
    }
  };

  const upload = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let failed = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api(`/api/game-reports/${bookingId}/material`, { method: "POST", body: fd, retries: 0 });
      } catch { failed += 1; }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    setNote(failed ? `Не загрузилось файлов: ${failed}` : "Медиа добавлено");
    load();
  };

  if (error) return <div className={css.mediaNote}>{error}</div>;
  if (items === null) return <div className={css.mediaNote}>Загрузка медиа…</div>;

  return (
    <div className={css.mediaBlock}>
      {items.length === 0 ? (
        <div className={css.mediaNote}>Медиа к этой игре пока нет — игроки ничего не увидят.</div>
      ) : (
        <ul className={css.mediaList}>
          {items.map((m) => (
            <li key={m.id} className={css.mediaRow}>
              <button type="button" className={css.mediaTh} title="Смотреть" onClick={() => { if (m.stream || m.poster) setView(m); }}>
                {m.poster ? <img src={m.poster} alt="" loading="lazy" /> : <span className={css.mediaThIco}>{m.kind === "audio" ? "♫" : m.kind === "video" ? "▶" : "🖼"}</span>}
                {m.kind === "video" && m.poster ? <span className={css.mediaThPlay}>▶</span> : null}
              </button>
              <span className={css.mediaName}>
                {m.label}
                <small>
                  {KIND_WORD[m.kind] ?? m.kind}
                  {m.duration ? ` · ${m.duration}` : ""}
                  {m.mine ? "" : ` · залил ${m.uploader}`}
                </small>
              </span>
              {confirmId === m.id ? (
                <span className={css.mediaConfirm}>
                  <button type="button" onClick={() => void remove(m)} disabled={busyId === m.id}>
                    {busyId === m.id ? "…" : "снять"}
                  </button>
                  <button type="button" onClick={() => setConfirmId(null)}>отмена</button>
                </span>
              ) : (
                <button type="button" className={css.mediaDel} onClick={() => setConfirmId(m.id)}>✕</button>
              )}
            </li>
          ))}
        </ul>
      )}

      <input ref={fileRef} type="file" accept="video/*,image/*,audio/*" multiple hidden
        onChange={(e) => void upload(e.target.files)} />
      <button type="button" className={css.mediaAdd} onClick={() => fileRef.current?.click()} disabled={uploading}>
        {uploading ? "Загружаю…" : "+ Добавить медиа"}
      </button>
      {view ? <MediaView m={view} onClose={() => setView(null)} /> : null}
      {note ? <div className={css.mediaNote} role="status">{note}</div> : null}
    </div>
  );
}
